import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { addNotification } from 'modules/notifications/redux';
import { NotificationTypes } from 'modules/notifications';
import { ValidationStatus } from 'modules/ui/components/DragAndDrop';
import { BoatsApi } from '../api';
import { BoatsActions, BoatsSelectors } from '../redux';
import { Boat } from '../types';

type FileValidationState = {
  status: ValidationStatus;
  error?: string;
};

export const useCreateOrUpdateBoat = (closeModal: () => void) => {
  const dispatch = useDispatch();
  const boat = useSelector(BoatsSelectors.firstBoat);
  const [polarsFile, setPolarFile] = useState<File | null>(null);
  const [sailsFile, setSailsFile] = useState<File | null>(null);
  const [waveFile, setWaveFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);

  const [polarsValidation, setPolarsValidation] = useState<FileValidationState>(
    { status: 'idle' },
  );
  const [sailsValidation, setSailsValidation] = useState<FileValidationState>({
    status: 'idle',
  });
  const [waveValidation, setWaveValidation] = useState<FileValidationState>({
    status: 'idle',
  });

  const validateFile = useCallback(
    async (
      file: File,
      type: 'polars' | 'sails' | 'wave',
      setValidation: (state: FileValidationState) => void,
    ) => {
      setValidation({ status: 'validating' });
      try {
        await BoatsApi.validateBoatFiles(file, type);
        setValidation({ status: 'valid' });
      } catch (err: any) {
        const message =
          err?.response?.data?.message || 'File validation failed';
        setValidation({ status: 'error', error: message });
      }
    },
    [],
  );

  const handleSetPolarFile = useCallback(
    (file: File) => {
      setPolarFile(file);
      validateFile(file, 'polars', setPolarsValidation);
    },
    [validateFile],
  );

  const handleSetSailsFile = useCallback(
    (file: File) => {
      setSailsFile(file);
      validateFile(file, 'sails', setSailsValidation);
    },
    [validateFile],
  );

  const handleSetWaveFile = useCallback(
    (file: File) => {
      setWaveFile(file);
      validateFile(file, 'wave', setWaveValidation);
    },
    [validateFile],
  );

  const handleUpdateOrCreate = useCallback(
    async ({ name }: { name: string }) => {
      setLoading(true);
      try {
        let updatedBoat: Boat = boat;
        if (boat) {
          updatedBoat = await BoatsApi.updateBoat({
            files: {
              polars: polarsFile,
              sails: sailsFile,
              wave: waveFile,
            },
            id: boat.id,
            name,
          });
        } else {
          updatedBoat = await BoatsApi.createBoat({
            files: {
              polars: polarsFile,
              sails: sailsFile,
              wave: waveFile,
            },
            name,
          });
        }

        dispatch(BoatsActions.setBoats([updatedBoat]));
        dispatch(
          addNotification({
            type: NotificationTypes.Success,
            message: `Boat has been ${
              boat ? 'updated' : 'created'
            } successfully.`,
          }),
        );
        closeModal();
      } catch (err) {
        dispatch(
          addNotification({
            type: NotificationTypes.Error,
            message: `Error while ${boat ? 'updating' : 'creating a'} boat.`,
          }),
        );
      } finally {
        setLoading(false);
      }
    },
    [dispatch, polarsFile, sailsFile, waveFile, boat, closeModal],
  );

  const hasValidationErrors =
    polarsValidation.status === 'error' ||
    sailsValidation.status === 'error' ||
    waveValidation.status === 'error';

  const isValidating =
    polarsValidation.status === 'validating' ||
    sailsValidation.status === 'validating' ||
    waveValidation.status === 'validating';

  return {
    loading,
    polarsFile,
    setPolarFile: handleSetPolarFile,
    sailsFile,
    setSailsFile: handleSetSailsFile,
    waveFile,
    setWaveFile: handleSetWaveFile,
    setLoading,
    handleUpdateOrCreate,
    boatExists: !!boat,
    initialName: boat?.name || '',
    polarsValidation,
    sailsValidation,
    waveValidation,
    hasValidationErrors,
    isValidating,
  };
};
